import { CurrencyPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  IndicatorComponent,
  SimpleTableColumn,
  SimpleTableComponent,
  SimpletableOptions,
} from '@kinet-open-network/client/shared/ui';
import {
  buildAddressString,
  orderStatusOptions,
} from '@kinet-open-network/shared/data-access/model';
import { ButtonModule } from 'primeng/button';
import { FlowOrders } from '../../data-access/flow-orders.model';
import { FlowOrdersService } from '../../data-access/flow-orders.service';

@Component({
  selector: 'kon-flow-orders-quick-view',
  imports: [
    IndicatorComponent,
    ButtonModule,
    CurrencyPipe,
    DatePipe,
    SimpleTableComponent,
  ],
  template: `
    @if (orderResource.isLoading()) {
      <div class="flex items-center gap-2 py-6 justify-center">
        <i class="pi pi-spin pi-spinner"></i>
        <span> Objednávka sa načítava...</span>
      </div>
    } @else if (orderResource.error(); as err) {
      <div
        class="flex flex-col py-6 px-6 text-danger-600 w-full divide-y-2 divide-gray-100"
      >
        <div class="flex items-center gap-2 w-full">
          <i class="pi pi-exclamation-triangle"></i>

          <div class="w-full font-semibold">
            @if ($any(err)?.error?.message; as ms) {
              {{ ms }}
            } @else {
              Chyba pri načítavaní objednávky.
            }
          </div>
        </div>
      </div>
    } @else if (orderResource.value(); as order) {
      <div class="flex flex-col gap-3 p-3">
        <div class="flex items-center justify-between gap-2">
          <h3 class="font-semibold text-lg">Objednávka č. {{ order.id }}</h3>
          <kon-indicator
            [value]="order.status"
            [options]="orderStatusOptions"
          />
        </div>
        <div class="flex flex-col gap-1 text-sm">
          <span class="font-medium">{{ order.customer?.fullname }}</span>
          @if (order.deliveryAddress) {
            <span>{{ addressString(order.deliveryAddress) }}</span>
          }
          @if (order.email) {
            <span>{{ order.email }}</span>
          }
          @if (order.phone) {
            <span>{{ order.phone }}</span>
          }
          <span>{{ order.date | date: 'dd.MM.yyyy' }}</span>
        </div>
        @if (orderResource.value()?.items; as items) {
          <kon-simple-table
            [data]="items"
            [columns]="columns"
            [options]="tableOptions"
          />
        }
        <div class="flex flex-col gap-1 text-sm border-t pt-2">
          <div class="flex justify-between font-semibold">
            <span>Spolu s DPH</span>
            <span>{{ order.total | currency: 'EUR' }}</span>
          </div>
        </div>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlowOrdersQuickViewComponent {
  private orderService = inject(FlowOrdersService);

  row = input<FlowOrders>();

  orderResource = rxResource({
    params: () => this.row()?.id,
    stream: ({ params: orderId }) => {
      return this.orderService.findOne(orderId);
    },
  });

  orderStatusOptions = orderStatusOptions;

  columns = [
    { header: 'Produkt', field: 'stockItem.name' },
    { header: 'Počet', field: 'quantity' },
    { header: 'Plná', field: 'isFull', type: 'boolean' },
    { header: 'Predaj', field: 'isSell', type: 'boolean' },
    { header: 'Reklamácia', field: 'isComplaint', type: 'boolean' },
  ] as SimpleTableColumn<FlowOrders['items'][number]>[];

  tableOptions = {} as SimpletableOptions;

  addressString(address: NonNullable<FlowOrders['deliveryAddress']>): string {
    return buildAddressString(address);
  }
}
